import { useState, Fragment } from "react";
import { useMutation } from "react-query";
import {
  Confirm,
  useNotify,
  useRecordContext,
  useDataProvider,
  Link,
  WithRecord,
  useResourceContext,
} from "react-admin";
import { ButtonStrong } from "../../components/buttonStrong";
import { Box, TextField } from "@mui/material";
import { WithPermissions } from "../../ra-lb-tools/acl/withPermissions";
import GridActionButton from "../../ra-lb-tools/components/buttonGridAction";
import { Cached, ManageHistory, Settings } from "@mui/icons-material";
import { humanize } from "../../ra-lb-tools/utils/tools";

const ResetTowerButtonRaw = ({ resource }) => {
  const record = useRecordContext();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { mutate, isLoading } = useMutation(() =>
    dataProvider.resetEvents(
      record.id,
      reason.trim() ? reason : null,
      notify,
      resource
    )
  );

  const handleClick = () => setOpen(true);
  const handleDialogClose = () => setOpen(false);
  const handleConfirm = () => {
    notify("Starting the Tower reset");
    mutate();
    setOpen(false);
  };

  const confirm_message = (
    <>
      <Box>
        Are you sure a factory reset was completed on tower: {record.id}?
      </Box>
      <Box>
        Resetting tower events here must be done in unison with a physical tower
        board reset. Resetting tower events here DOES NOT replace a physical
        tower reset.
      </Box>
      <Box>
        Resetting events only here, or at different times from the reset here,
        will result in sync issues that will require a physical visit to the
        tower to resolve.
      </Box>
      <Box>
        Do not do this unless you are absolutely sure. For additional
        information{" "}
        <Link
          target="_blank"
          to="https://docs.harborlockers.com/common_issues.html#factory-reset-without-using-the-app"
        >
          see this
        </Link>
        .
      </Box>
      <Box>The board has been factory reset?</Box>
      <Box>
        <TextField
          label="Reset Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          rows={2}
          variant="outlined"
          sx={{ marginTop: "10px" }}
        />
      </Box>
    </>
  );
  return (
    <Fragment>
      <ButtonStrong
        label="Report tower reset"
        onClick={handleClick}
        color="inherit"
        size="small"
        sx={{ flexShrink: 0 }}
      />
      <Confirm
        isOpen={open}
        loading={isLoading}
        title="WARNING! READ THIS"
        content={confirm_message}
        onConfirm={handleConfirm}
        onClose={handleDialogClose}
        sx={{
          "*": {
            paddingBottom: "5px",
          },
        }}
        confirm={"YES"}
        cancel={"NO"}
        confirmColor="green"
      />
    </Fragment>
  );
};

export const ResetTowerButton = ({ resource }) => {
  return (
    <WithPermissions
      can={{ resource, action: "reset_events" }}
      label={"ResetTowerButton"}
    >
      <WithPermissions
        className="ResetEventsButton"
        can={{ roles: ["superuser"] }}
      >
        <WithRecord
          render={(tower) =>
            tower.isForTest && <ResetTowerButtonRaw resource={resource} />
          }
        />
      </WithPermissions>
      <WithPermissions
        className="ResetEventsButtonConsole"
        can={{ roles: ["-superuser"] }}
      >
        <ResetTowerButtonRaw resource={resource} />
      </WithPermissions>
    </WithPermissions>
  );
};

export const ReleaseLockersButton = ({ ...props }) => {
  const resource = useResourceContext(props);
  const action = "release-lockers";
  const record = useRecordContext();
  const url = `/${resource}/${record.id}/${action}`;
  const label = "Release Lockers";
  const getConfirmationText = (tower) => (
    <Box>
      <Box>{`You are about to mark as "Available" all lockers for tower '${tower.name}' (#${tower.id})`}</Box>
      <Box>{`This action can not be undone`}</Box>
      <Box>{`Proceed?`}</Box>
    </Box>
  );

  return (
    <WithPermissions can={{ resource, action }} label={"ReleaseLockersButton"}>
      <GridActionButton
        url={url}
        label={label}
        {...props}
        confirm={true}
        getConfirmationText={getConfirmationText}
      />
    </WithPermissions>
  );
};

export const FactoryResetTowerButton = ({ ...props }) => (
  <OnlineActionButton
    action="factory-reset"
    permissionAction="factory_reset_online_tower"
    startIcon={<ManageHistory />}
    successMessage="Tower factory reset completed"
    {...props}
  />
);

export const ConfigTowerButton = ({ ...props }) => (
  <OnlineActionButton
    action="config"
    permissionAction="config_online_tower"
    startIcon={<Settings />}
    successMessage="Tower online config completed"
    {...props}
  />
);

export const SyncTowerButton = ({ ...props }) => (
  <OnlineActionButton
    action="sync"
    permissionAction="sync_online_tower"
    startIcon={<Cached />}
    successMessage="Tower synced successfully"
    {...props}
  />
);

const OnlineActionButton = ({ action, permissionAction, label, ...props }) => {
  const resource = useResourceContext(props);

  const tower = useRecordContext();
  if (!tower) return null;
  if (!tower.hasOnlineHardware) return null;

  const url = `/${resource}/${tower.id}/${action}`;
  label = label || humanize(action);

  return (
    <WithPermissions
      can={{ resource, action: permissionAction }}
      label={`OnlineActionButton-${action}`}
    >
      <GridActionButton
        url={url}
        method="PUT"
        label={label}
        confirm={false}
        {...props}
      />
    </WithPermissions>
  );
};
